import React, { useState } from 'react';

interface PieChartProps {
  data: { name: string; value: number }[];
  colors?: string[];
  width?: number | string;
  height?: number | string;
  activeIndex?: number | null;
  onSliceHover?: (index: number | null) => void;
}

const PieChartComponent: React.FC<PieChartProps> = ({
  data,
  colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'],
  width = 310,
  height = 310,
  activeIndex,
  onSliceHover,
}) => {
  const [localHoveredIndex, setLocalHoveredIndex] = useState<number | null>(null);
  const hoveredIndex = activeIndex !== undefined ? activeIndex : localHoveredIndex;

  const setHoveredSlice = (index: number | null) => {
    if (activeIndex === undefined) {
      setLocalHoveredIndex(index);
    }
    if (onSliceHover) {
      onSliceHover(index);
    }
  };
  const total = data.reduce((sum, item) => sum + item.value, 0) || 1;

  // Build SVG path arcs for each slice
  let cumulative = 0;
  const radius = 135;
  const center = 155;

  const slices = data.map((item, index) => {
    const value = item.value;
    if (value <= 0) {
      return null;
    }

    const hasActiveHover = hoveredIndex !== null;
    const isHovered = hoveredIndex === index;
    const fillColor = hasActiveHover && !isHovered ? '#c9c9c9' : colors[index % colors.length];

    // SVG arc paths cannot represent a true 360-degree sweep (start==end),
    // so render a full circle when a single segment takes 100%.
    if (value >= total) {
      const hoverOffsetY = isHovered ? -8 : 0;
      return (
        <circle
          key={`${item.name}-${index}`}
          cx={center}
          cy={center + hoverOffsetY}
          r={radius}
          fill={fillColor}
          stroke="#ffffff"
          strokeWidth={1}
          style={{
            transition: 'all 180ms ease',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setHoveredSlice(index)}
          onMouseLeave={() => setHoveredSlice(null)}
        />
      );
    }

    const startAngle = (cumulative / total) * Math.PI * 2;
    const endAngle = ((cumulative + value) / total) * Math.PI * 2;
    cumulative += value;

    const x1 = center + radius * Math.cos(startAngle - Math.PI / 2);
    const y1 = center + radius * Math.sin(startAngle - Math.PI / 2);
    const x2 = center + radius * Math.cos(endAngle - Math.PI / 2);
    const y2 = center + radius * Math.sin(endAngle - Math.PI / 2);
    const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;
    const midAngle = (startAngle + endAngle) / 2;
    const hoverOffsetX = isHovered ? Math.cos(midAngle - Math.PI / 2) * 8 : 0;
    const hoverOffsetY = isHovered ? Math.sin(midAngle - Math.PI / 2) * 8 : 0;

    const d = [
      `M ${center} ${center}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      'Z',
    ].join(' ');

    return (
      <path
        key={`${item.name}-${index}`}
        d={d}
        fill={fillColor}
        stroke="#ffffff"
        strokeWidth={1}
        transform={`translate(${hoverOffsetX}, ${hoverOffsetY})`}
        style={{
          transition: 'all 180ms ease',
          cursor: 'pointer',
        }}
        onMouseEnter={() => setHoveredSlice(index)}
        onMouseLeave={() => setHoveredSlice(null)}
      />
    );
  });

  return (
    <div
      style={{
        width,
        height,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <svg width={center * 2} height={center * 2} viewBox={`0 0 ${center * 2} ${center * 2}`}>
        {slices}
        {/* Inner circle to create donut effect */}
        <circle cx={center} cy={center} r={radius * 0.6} fill="#ffffff" />
      </svg>
    </div>
  );
};

export default PieChartComponent;